<!--
 * @Description: 个体户营业执照相关信息。与单独添加共用。
                 区别：单独添加又服务有效期，相关信息中没有
 * @Date: 2025-09-12 17:49:40
-->
<template>
  <BaseTextTitle v-if="isDetail">个体户营业执照相关信息</BaseTextTitle>
  <BaseTextContent>
    <BaseForm
      ref="ruleFormRef"
      label-position="top"
      :model="formData"
      :rules="rules"
      :column-count="columnCount"
    >
      <BaseFormItem label="所属园区" prop="businessParkId">
        <BaseSelect
          style="width: 100%"
          placeholder="请选择园区"
          label-key="businessParkName"
          value-key="id"
          :filterUseRemote="true"
          :init="true"
          :disabled="disabled"
          :remoteSelected="[
            {
              id: formData.businessParkId,
              businessParkName: formData.businessParkName
            }
          ]"
          :remoteMethod="parkRemoteMethod"
          v-model="formData.businessParkId"
          @change="onChangePark"
        />
      </BaseFormItem>
      <BaseFormItem label="经营者" prop="operator">
        <BaseInput
          style="width: 100%"
          placeholder="请输入经营者名称"
          show-word-limit
          maxlength="50"
          v-model="formData.operator"
          :disabled="disabled"
        />
      </BaseFormItem>
      <BaseFormItem label="手机号" prop="phone">
        <BaseInput
          style="width: 100%"
          placeholder="请输入登录手机号"
          type="tel"
          show-word-limit
          maxlength="11"
          v-model="formData.phone"
          :disabled="disabled"
        />
      </BaseFormItem>
      <BaseFormItem label="个体户名称" prop="individualName">
        <BaseInput
          style="width: 100%"
          placeholder="请输入个体户名称"
          show-word-limit
          maxlength="50"
          v-model="formData.individualName"
          :disabled="disabled"
        />
      </BaseFormItem>
      <BaseFormItem label="统一社会信用代码" prop="entpCertNo">
        <BaseInput
          style="width: 100%"
          placeholder="请输入对应的证件码"
          show-word-limit
          maxlength="18"
          v-model="formData.entpCertNo"
          :disabled="disabled"
        />
      </BaseFormItem>
      <BaseFormItem
        label="注册日期"
        prop="registerAt"
        :rules="[
          {
            required: disabled && formData.individualType === 2 ? false : true, // 客户个体类型查看详情不校验
            message: '请选择',
            trigger: 'change'
          }
        ]"
      >
        <BaseDatePicker
          style="width: 100%"
          placeholder="请选择"
          type="date"
          validate-event
          v-model="formData.registerAt"
          :disabled="disabled"
          :disabledDate="disabledDate"
          @change="onChangeRegisterAt"
        />
      </BaseFormItem>
      <!-- 页面布局占位 -->
      <div v-if="columnCount == 4"></div>
      <div v-if="columnCount == 4"></div>
      <BaseFormItem label="营业执照" prop="_fileList">
        <BaseUpload
          style="width: 100%"
          listType="picture-card"
          tipText="支持上传jpg/png文件，且不超过50MB"
          v-model:fileList="formData._fileList"
          :disabled="disabled"
          :auto-upload="true"
          :multiple="false"
          :http-request="uploadApi"
          :showFileList="true"
          :limit="1"
          :limitSize="50"
          :onError="onError"
          :accept="['.jpg', '.png', '.jpeg', '.JPG', '.PNG', '.JPEG']"
          @change="onEntpCertPicChange"
        />
      </BaseFormItem>
      <BaseFormItem label="经营范围" prop="businessScope">
        <BaseInput
          style="width: 100%"
          placeholder="请填写经营范围"
          type="textarea"
          maxlength="1000"
          :show-word-limit="true"
          :rows="9"
          :disabled="disabled"
          v-model="formData.businessScope"
        />
      </BaseFormItem>
      <BaseFormItem label="经营场所" prop="businessAddress">
        <BaseInput
          style="width: 100%"
          placeholder="请填写经营场所"
          type="textarea"
          maxlength="1000"
          :show-word-limit="true"
          :rows="9"
          :disabled="disabled"
          v-model="formData.businessAddress"
        />
      </BaseFormItem>
      <!-- 页面布局占位 -->
      <div v-if="columnCount == 4"></div>
      <BaseFormItem label="征收方式" prop="taxAssessmentMethod">
        <BaseSelect
          style="width: 100%"
          placeholder="请选择征收方式"
          v-model="formData.taxAssessmentMethod"
          :options="TAX_ASSESSMENT_METHOD"
          :disabled="editDisabled"
        />
      </BaseFormItem>
      <BaseFormItem label="申报周期" prop="declareMethod">
        <BaseSelect
          style="width: 100%"
          placeholder="请选择申报周期"
          v-model="formData.declareMethod"
          :options="DECLARE_METHOD"
          :disabled="editDisabled"
        />
      </BaseFormItem>
      <BaseFormItem label="发票类型" prop="_invoiceType">
        <BaseCheckboxGroup
          v-model="formData._invoiceType"
          :disabled="editDisabled"
        >
          <BaseCheckbox
            v-for="item in [INVOICE_TYPE[1], INVOICE_TYPE[2]]"
            :key="item.value"
            :value="item.value"
            :label="item.label"
          >
            {{ item.label }}
          </BaseCheckbox>
        </BaseCheckboxGroup>
      </BaseFormItem>
      <!-- 仅新增个体时有该项。详情该项为独立分类 -->
      <BaseFormItem label="服务有效期" prop="_expira" v-if="!isDetail">
        <div style="width: 100%; display: flex; justify-content: space-between">
          <div style="width: 80%">
            <BaseDatePicker
              style="width: 100%"
              type="daterange"
              startPlaceholder="开始时间"
              endPlaceholder="结束时间"
              validate-event
              :disabled="disabled || !!formData.isPermanent"
              v-model="formData._expira"
            />
          </div>
          <BaseCheckbox
            :true-value="1"
            :false-value="0"
            v-model="formData.isPermanent"
            @change="onChangePermanent"
          >
            长期
          </BaseCheckbox>
        </div>
      </BaseFormItem>
      <BaseFormItem label="个体户类别" prop="individualType">
        <BaseSelect
          style="width: 100%"
          placeholder="请选择个体户类别"
          :options="_INDIVIDUAL_TYPE"
          :disabled="disabled"
          v-model="formData.individualType"
          @change="onChangeIndividualType"
        />
      </BaseFormItem>
      <BaseFormItem v-if="showGt2Add" label="个体地址" prop="districtId">
        <BaseCascader
          style="width: 100%"
          clearable
          filterable
          placeholder="请选择证照地址"
          ref="areaCascaderRef"
          :options="areaOptions"
          :props="{
            value: 'id',
            label: 'name',
            children: 'children'
          }"
          :disabled="disabled"
          v-model="areaModel"
          @change="onChangeArea"
        ></BaseCascader>
      </BaseFormItem>
      <BaseFormItem
        :style="{
          'grid-column-start': 1,
          'grid-column-end': 4,
          'margin-bottom': '20px'
        }"
        label="开票类目"
        prop="invoiceItemIdList"
      >
        <div style="width: 100%">
          <div
            v-for="(item, index) in formData.invoiceItemIdList"
            :key="index"
            style="
              width: 100%;
              margin-bottom: 10px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            "
          >
            <BaseInput
              style="flex: 1; margin-right: 10px"
              placeholder="请选择"
              disabled
              v-model="item.label"
            >
              <template #prepend>一级类目</template>
            </BaseInput>
            <BaseInput
              style="flex: 1; margin-right: 10px"
              placeholder="请选择"
              disabled
              :model-value="(item.subItem || []).map(s => s.name).join('、')"
            >
              <template #prepend>二级类目</template>
            </BaseInput>
            <BaseButton
              type="text"
              @click="onInvoiceItemRemove(index)"
              :disabled="editDisabled"
            >
              删除
            </BaseButton>
          </div>
          <BaseButton
            type="text"
            @click="onInvoiceItemAdd"
            :disabled="editDisabled"
          >
            添加
          </BaseButton>
        </div>
      </BaseFormItem>
    </BaseForm>
  </BaseTextContent>
</template>
<script lang="tsx" setup>
import { ref, computed, watch } from 'vue';
import {
  BaseForm,
  BaseFormItem,
  BaseInput,
  BaseButton,
  BaseSelect,
  BaseDatePicker,
  BaseUpload,
  XrkMessage,
  BaseFormRef,
  BaseTextTitle,
  BaseTextContent,
  BaseCheckboxGroup,
  BaseCheckbox,
  useCreateModal,
  BaseCascader
} from 'xrk-components';
import { parkPageList } from '@data-source/park/apis';
import { uploadApi } from '@data-source/common/apis';
import { uploadLicense } from '../../data-source/apis';
import {
  INVOICE_TYPE,
  DECLARE_METHOD,
  TAX_ASSESSMENT_METHOD,
  INDIVIDUAL_TYPE
} from '../../constants';
import {
  AddIndividualParams,
  IndividualInfoDetailData
} from '../../data-source/types';
import AddCategoryDialog from './add-category-dialog.vue';
import { useDateFormat } from '@vueuse/core';
import { areaList } from '@data-source/common/apis';

const NOW = Date.now();
const _INDIVIDUAL_TYPE = [INDIVIDUAL_TYPE[3]];

const props = withDefaults(
  defineProps<{
    remove?: () => {};
    type: 'detail' | 'add'; // 1个体相关信息 2单独添加
    columnCount: number;
  }>(),
  {
    type: 'detail',
    columnCount: 4
  }
);

const isDetail = props.type === 'detail';

const formData = defineModel<IndividualInfoDetailData | AddIndividualParams>();
const ruleFormRef = ref<BaseFormRef>();
const areaOptions = ref([]); // 选项
const areaModel = ref([]); // 绑定值
// 选择后的id+name集合
const areaVal = ref({
  provinceId: null,
  cityId: null,
  districtId: null,
  provinceName: null,
  cityName: null,
  districtName: null
});
const areaCascaderRef = ref(null);

// 详情中，可编辑且可补充的
const disabled = computed(() => {
  if (!isDetail) return false; // 非详情可编辑
  // 查看详情，平台审核后可补充操作
  if ([21].includes(+(formData.value as IndividualInfoDetailData)?.auditStatus))
    return false;
  return true;
});
// 详情中，终审通过后可保存操作的
const editDisabled = computed(() => {
  const auditStatus = +(formData.value as IndividualInfoDetailData)
    ?.auditStatus;
  if (!isDetail) return false; // 非详情可编辑
  if ([21, 22].includes(auditStatus)) return false; // 终审中与通过可编辑
  return true;
});
const showGt2Add = computed(() => {
  // !isDetail &&
  return formData.value.individualType == 2;
});

/* registerAt单独行内处理 */
const rules = {
  businessParkId: [
    {
      required: true,
      message: '请填写',
      trigger: 'change'
    }
  ],
  operator: [
    {
      required: true,
      message: '请填写',
      trigger: 'change'
    }
  ],
  phone: [
    {
      required: true,
      message: '请填写',
      trigger: 'change'
    }
  ],
  individualName: [
    {
      required: true,
      message: '请填写',
      trigger: 'change'
    }
  ],
  entpCertNo: [
    {
      required: true,
      message: '请填写',
      trigger: 'change'
    }
  ],

  _fileList: [
    {
      required: true,
      validator: (rule: any, value: any, callback: any) => {
        if (!value.length) {
          callback(new Error('请上传'));
        }
        callback();
      },
      trigger: 'change'
    }
  ],
  taxAssessmentMethod: [
    {
      required: true,
      message: '请选择',
      trigger: 'change'
    }
  ],
  declareMethod: [
    {
      required: true,
      message: '请选择',
      trigger: 'change'
    }
  ],
  _invoiceType: [
    {
      required: true,
      message: '请选择',
      trigger: 'change'
    }
  ],
  _expira: [
    {
      required: true,
      message: '请选择',
      trigger: 'change',
      validator: (rule: any, value: any, callback: any) => {
        if (!formData.value.isPermanent && !value.length) {
          callback(new Error('请选择'));
        }
        callback();
      }
    }
  ],
  individualType: [
    {
      required: true,
      message: '请选择',
      trigger: 'change'
    }
  ],
  invoiceItemIdList: [
    {
      required: true,
      validator: (rule: any, value: any, callback: any) => {
        if (!value.length) {
          callback(new Error('请选择'));
        }
        callback();
      },
      trigger: 'change'
    }
  ],
  districtId: [
    {
      required: true,
      message: '请选择',
      trigger: 'change'
    }
  ],
  businessScope: [
    {
      required: true,
      message: '请填写',
      trigger: 'change'
    }
  ],
  businessAddress: [
    {
      required: true,
      message: '请填写',
      trigger: 'change'
    }
  ]
};

watch(
  () => formData.value,
  v => {
    const { provinceId, cityId, districtId } = v;
    if (provinceId && cityId && districtId) {
      areaModel.value = [provinceId, cityId, districtId];
    }
  },
  {
    once: true
  }
);

const disabledDate = date => {
  return date.getTime() >= NOW;
};
const parkRemoteMethod = async val => {
  return parkPageList({
    businessParkName: val,
    pageSize: 200,
    enable: 1 // 筛选启用的数据
  });
};
const getAreaList = async () => {
  const { data } = await areaList();
  areaOptions.value = data;
};

const onChangeRegisterAt = () => {
  /* 单独添加，联动无值的服务有效期 
     详情中不在此处处理
  */
  if (props.type === 'add' && !formData.value._expira.length) {
    formData.value._expira = [
      formData.value.registerAt,
      useDateFormat(
        new Date(
          new Date(formData.value.registerAt).getTime() +
            (365 - 1) * 24 * 60 * 60 * 1000
        ),
        'YYYY-MM-DD'
      ).value
    ];
  }
};
const onChangePark = (value, [park]) => {
  (formData.value as AddIndividualParams).parkType = park.parkType;
};
const onEntpCertPicChange = async file => {
  await ruleFormRef.value.validateField('_fileList');

  // 营业执照图片上传完成后识别信息
  if (file?.response?.data?.fileUrl) {
    try {
      const { data } = await uploadLicense({
        url: file.response.data.fileUrl
      });
      // 识别成功则填充相关信息
      if (data) {
        formData.value.individualName = data.individualName || '';
        formData.value.entpCertNo = data.entpCertNo || '';
        formData.value.registerAt = data.registerAt || '';
        formData.value.businessScope = data.businessScope || '';
        formData.value.businessAddress = data.businessAddress || '';
        XrkMessage.success('营业执照识别成功');
      }
    } catch (error) {
      console.error('营业执照识别失败:', error);
    }
  }
};
const onInvoiceItemRemove = index => {
  formData.value.invoiceItemIdList.splice(index, 1);
};
const onInvoiceItemAdd = () => {
  useCreateModal(AddCategoryDialog, {
    invoiceItemIdList: formData.value.invoiceItemIdList,
    onSuccess: payload => {
      formData.value.invoiceItemIdList.push(payload);
      ruleFormRef.value.validateField('invoiceItemIdList');
    }
  });
};
const onError = errorInfo => {
  XrkMessage.error(errorInfo.msg);
};
const onChangeIndividualType = v => {
  // 客户个体默认服务有效期为长期，如果没有选择时间的话
  if (v === 2 && !formData.value._expira.length) {
    formData.value.isPermanent = 1;
  }
};
const onChangeArea = () => {
  const [nodes] = areaCascaderRef.value.getCheckedNodes();
  const { pathLabels, pathValues } = nodes;
  areaVal.value.provinceId = pathValues[0];
  areaVal.value.cityId = pathValues[1];
  areaVal.value.districtId = pathValues[2];
  areaVal.value.provinceName = pathLabels[0];
  areaVal.value.cityName = pathLabels[1];
  areaVal.value.districtName = pathLabels[2];
  Object.assign(formData.value, areaVal.value);
};
const onChangePermanent = val => {
  if (val) {
    formData.value._expira = [];
  }
};

getAreaList();

defineExpose({
  validate: () => {
    return ruleFormRef.value.validate();
  }
});
</script>
